import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {RuntimeLoader} from "@rive-app/react-canvas-lite";
import * as styles from "./styles/PageOnboarding.module.css";
import LoadGuard from "../view/LoadGuard";
import AT from "../vault/ActionTypes";
import withVaultDispatch from "../hoc/withVaultDispatch.jsx";
import OpsUser from "../vault/OpsUser";
import {titleCaseName} from "../../parent/primaryRegistration/studentNameDisplay";
import {SCHOOL_BACKGROUND_WASM_URL} from "../../parent/primaryRegistration/schoolBackgroundAsset";
import {
	getMixpanelDataFromUser,
	getPlacementLevelAndChapter,
	OnboardingState,
	OnboardingStep,
	useSchoolBackgroundAnimation,
} from "./util/PageOnboardingUtil";
import {track} from "../util/Mixpanel.js";
import {
	OnboardingStepChooseALevel,
	OnboardingStepPlacementTestLoadError,
	OnboardingStepInProgress,
	OnboardingStepNotStarted,
	OnboardingStepParentManuallySelectedLevel,
	OnboardingStepReadyToStart,
	OnboardingStepTestComplete,
} from "./PageOnboardingSteps";
import {User} from "shared/types/VaultTypes";

RuntimeLoader.setWasmUrl(SCHOOL_BACKGROUND_WASM_URL);

type PageOnboardingProps = {
	user: User;
	name: string; // page.name, but PageComponent renders this component with {...page} spread as an argument.
	dispatch: (action: any) => void;
};

export const PageOnboarding = withVaultDispatch(
	({user, dispatch}: PageOnboardingProps) => {
		const [hasInitialized, setHasInitialized] = useState(false);
		const loadUserPlacementTest = useCallback(() => {
			dispatch({type: AT.LOAD_USER_PLATFORM_PLACEMENT_REQUEST});
		}, [dispatch]);

		const placementTestInfo = user.placementTest;
		const [onboardingState, setOnboardingState] = useState<OnboardingState>({
			step: "NOT_STARTED",
		});

		const shouldSkipPlacementTest = useMemo(() => {
			const studentOnboardingProgress = OpsUser.getStudentOnboardingProgress(
				user
			);
			return studentOnboardingProgress?.currentStep === "manualLevelSelection";
		}, [user]);

		// ==============================================================================================
		// Handle initialization once placement test has loaded, errored, or we're skipping placementTest.
		// ==============================================================================================
		useEffect(() => {
			if (hasInitialized) return;
			if (
				placementTestInfo.load === "none" ||
				placementTestInfo.load === "pending"
			) {
				return;
			}

			let initialOnboardingStep: OnboardingStep = "NOT_STARTED";
			/** === Parent Manually chose level: Skipping Placement Test === */
			if (shouldSkipPlacementTest) {
				initialOnboardingStep = "PARENT_MANUALLY_SELECTED_LEVEL";
			} else if (placementTestInfo.load === "error") {
				/** === ERROR When Loading Placement Test. === */
				initialOnboardingStep = "PLACEMENT_TEST_LOAD_ERROR";
			} else if (placementTestInfo.load === "done") {
				/** === Placement Test Loaded Successfully === */
				if (
					// Fetching the initial value of the onboardingStep from the retrieved placementTestInfo.
					placementTestInfo.previousResults?.results &&
					placementTestInfo.previousResults.results.length > 0
				) {
					initialOnboardingStep = "TEST_COMPLETE";
				} else if (placementTestInfo.inProgress) {
					initialOnboardingStep = "IN_PROGRESS";
				} else {
					initialOnboardingStep = "NOT_STARTED";
				}
			} else {
				const exhaustiveCheck: never = placementTestInfo.load;
				throw new Error(
					`Unhandled placementTestInfo load case: ${exhaustiveCheck}`
				);
			}

			setHasInitialized(true);
			setOnboardingState({step: initialOnboardingStep});
			track("Student Onboarding: Impression", {
				step: initialOnboardingStep,
				...getMixpanelDataFromUser(user),
			});
		}, [shouldSkipPlacementTest, placementTestInfo, user, hasInitialized]);

		const StepComponent = useMemo(() => {
			// Roughly in order that the user may see.
			switch (onboardingState.step) {
				case "NOT_STARTED":
					return OnboardingStepNotStarted;
				case "IN_PROGRESS":
					return OnboardingStepInProgress;
				case "PLACEMENT_TEST_LOAD_ERROR":
					return OnboardingStepPlacementTestLoadError;
				case "CHOOSE_A_LEVEL":
					return OnboardingStepChooseALevel;
				case "TEST_COMPLETE":
					return OnboardingStepTestComplete;
				case "PARENT_MANUALLY_SELECTED_LEVEL":
					return OnboardingStepParentManuallySelectedLevel;
				case "DOOR_OPENING_ANIMATION": {
					if (
						onboardingState.manualLevelSelection?.from ===
						"PARENT_MANUALLY_SELECTED_LEVEL"
					) {
						return OnboardingStepParentManuallySelectedLevel;
					} else if (
						onboardingState.manualLevelSelection?.from === "CHOOSE_A_LEVEL"
					) {
						return OnboardingStepChooseALevel;
					} else {
						if (placementTestInfo.load === "error") {
							return OnboardingStepPlacementTestLoadError;
						} else {
							return OnboardingStepTestComplete;
						}
					}
				}
				case "READY_TO_START":
					return OnboardingStepReadyToStart;
				default:
					const exhaustiveCheck: never = onboardingState.step;
					throw new Error(`Unhandled Onboarding Step: ${exhaustiveCheck}`);
			}
		}, [onboardingState, placementTestInfo.load]);

		const containerRef = useRef<HTMLDivElement | null>(null);
		const {
			rive,
			RiveComponent,
			setRiveLevelPlaque,
		} = useSchoolBackgroundAnimation({containerRef});

		const levelPlacement =
			onboardingState.manualLevelSelection ??
			getPlacementLevelAndChapter(placementTestInfo, user);
		useEffect(() => {
			if (rive && levelPlacement.level) {
				const levelPlaqueArtboardName = `BA0${levelPlacement.level}_Plaque_Shine_Animation`;
				const levelPlaqueToUse = rive.getBindableArtboard(
					levelPlaqueArtboardName
				);
				setRiveLevelPlaque(levelPlaqueToUse);
			}
		}, [rive, levelPlacement.level, setRiveLevelPlaque]);

		const renderView = useCallback(
			() => (
				<div className={styles.onboarding} ref={containerRef}>
					<RiveComponent className={styles.animatedScene} />

					<StepComponent
						displayName={titleCaseName(user.model!.displayName)}
						dispatch={dispatch}
						rive={rive}
						user={user}
						placementTestInfo={placementTestInfo}
						onboardingState={onboardingState}
						setOnboardingState={setOnboardingState}
					/>
				</div>
			),
			[
				rive,
				RiveComponent,
				StepComponent,
				dispatch,
				onboardingState,
				placementTestInfo,
				user,
			]
		);
		return shouldSkipPlacementTest ? (
			renderView()
		) : (
			<LoadGuard
				identifier={`user${user.userID}-placementTest`}
				status={user.placementTest}
				requestLoad={() => loadUserPlacementTest()}
				loadingView="logo"
				logoColorType="white"
				// N.B. Only using LoadGuard for the requestLoad & loadingView.
				// If placement test load errors, we gracefully handle it in
				// the initialization useEffect, and render the appropriate
				// step. Therefore, still render the normal view here..
				errorView={renderView()}
				renderLoaded={renderView}
			/>
		);
	}
);
